import React, { useState } from 'react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";

interface NotificationsTabProps {
  emailNotifications?: boolean;
  setEmailNotifications?: (value: boolean) => void;
  tradingAlerts?: boolean;
  setTradingAlerts?: (value: boolean) => void;
  marketUpdates?: boolean;
  setMarketUpdates?: (value: boolean) => void;
  securityAlerts?: boolean;
  setSecurityAlerts?: (value: boolean) => void;
  productUpdates?: boolean;
  setProductUpdates?: (value: boolean) => void;
  newFeatures?: boolean;
  setNewFeatures?: (value: boolean) => void;
}

// Main component implementation that works for both export styles
const NotificationsTabComponent = ({
  emailNotifications = true,
  setEmailNotifications = () => {},
  tradingAlerts = true, 
  setTradingAlerts = () => {},
  marketUpdates = false,
  setMarketUpdates = () => {},
  securityAlerts = true,
  setSecurityAlerts = () => {},
  productUpdates = false,
  setProductUpdates = () => {},
  newFeatures = true,
  setNewFeatures = () => {}
}: NotificationsTabProps) => {
  const { toast } = useToast();

  const handleSave = () => {
    toast({
      title: "Notification settings saved",
      description: "Your notification preferences have been updated successfully."
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-100 mb-2">Notification Settings</h2>
        <p className="text-slate-400">
          Control how and when you receive notifications from Stewie Trader.
        </p>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-medium text-slate-200 mb-4">Email Notifications</h3>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <Label className="text-slate-300">Email Address</Label>
              <p className="text-xs text-slate-500">All notifications will be sent to this email</p>
            </div>
            <Input 
              value="user@stewietrader.com"
              readOnly
              className="max-w-[250px] bg-slate-600 border-slate-500 text-slate-200"
            />
          </div>
          
          <div className="flex items-center justify-between pt-4">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Trade Confirmations</Label>
              <p className="text-xs text-slate-500">Receive email when a trade is executed</p>
            </div>
            <Switch 
              checked={emailNotifications}
              onCheckedChange={setEmailNotifications}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Price Alerts</Label>
              <p className="text-xs text-slate-500">Get notified when price alerts are triggered</p>
            </div>
            <Switch 
              checked={tradingAlerts}
              onCheckedChange={setTradingAlerts}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Portfolio Updates</Label>
              <p className="text-xs text-slate-500">Daily summary of portfolio performance</p>
            </div>
            <Switch 
              checked={marketUpdates}
              onCheckedChange={setMarketUpdates}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Security Alerts</Label>
              <p className="text-xs text-slate-500">Get notified about account security events</p>
            </div>
            <Switch 
              checked={securityAlerts}
              onCheckedChange={setSecurityAlerts}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Marketing & Updates</Label>
              <p className="text-xs text-slate-500">News about features and promotions</p>
            </div>
            <Switch 
              checked={productUpdates}
              onCheckedChange={setProductUpdates}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-medium text-slate-200 mb-4">In-App Notifications</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Trade Alerts</Label>
              <p className="text-xs text-slate-500">Show notifications when trades execute</p>
            </div>
            <Switch 
              checked={tradingAlerts}
              onCheckedChange={setTradingAlerts}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Price Notifications</Label>
              <p className="text-xs text-slate-500">Show alerts for price movements</p>
            </div>
            <Switch 
              checked={marketUpdates}
              onCheckedChange={setMarketUpdates}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">System Messages</Label>
              <p className="text-xs text-slate-500">Important system status updates</p>
            </div>
            <Switch 
              checked={securityAlerts}
              onCheckedChange={setSecurityAlerts}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Sound Effects</Label>
              <p className="text-xs text-slate-500">Play sounds for important notifications</p>
            </div>
            <Switch 
              checked={newFeatures}
              onCheckedChange={setNewFeatures}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-medium text-slate-200 mb-4">Mobile Push Notifications</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-300 cursor-pointer">Enable Push Notifications</Label>
              <p className="text-xs text-slate-500">Receive notifications on your mobile device</p>
            </div>
            <Switch 
              checked={productUpdates}
              onCheckedChange={setProductUpdates}
              className="data-[state=checked]:bg-blue-600" 
            />
          </div>
          
          <div className="bg-blue-600/10 border border-blue-600/30 rounded-md p-3 text-sm text-slate-400">
            <p>
              Mobile push notifications are available through the Stewie Trader mobile app. 
              Download the app from the App Store or Google Play to enable this feature.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white">
          Save Notification Settings
        </Button>
      </div>
    </div>
  );
};

// Named export for ModernSettings.tsx
export function NotificationsTab(props: NotificationsTabProps) {
  return <NotificationsTabComponent {...props} />;
}

// Default export for Settings.tsx
export default function DefaultNotificationsTab() {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [tradingAlerts, setTradingAlerts] = useState(true);
  const [marketUpdates, setMarketUpdates] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState(true);
  const [productUpdates, setProductUpdates] = useState(false);
  const [newFeatures, setNewFeatures] = useState(true);

  return (
    <NotificationsTabComponent
      emailNotifications={emailNotifications}
      setEmailNotifications={setEmailNotifications}
      tradingAlerts={tradingAlerts}
      setTradingAlerts={setTradingAlerts}
      marketUpdates={marketUpdates}
      setMarketUpdates={setMarketUpdates}
      securityAlerts={securityAlerts}
      setSecurityAlerts={setSecurityAlerts}
      productUpdates={productUpdates}
      setProductUpdates={setProductUpdates}
      newFeatures={newFeatures}
      setNewFeatures={setNewFeatures}
    />
  );
}